import WorkflowLocationPanel from './WorkflowLocationPanel'; // UI-assettabs: mỗi bối cảnh một panel đầy bề rộng (khung Sky)
import { AssetTabHead, AssetAddButton, AssetHeadButton } from './AssetTabHead';
import React, { useEffect, useState, useCallback } from 'react';
import { Button, message, Modal, Input, Popconfirm } from 'antd';
import { ReloadOutlined, PlusOutlined } from '@ant-design/icons';
import { filmApi } from '../../services/api/film';
import { takeWorkflowHandoff } from '../../services/api/workflowReadHandoff';
import { AK, locationIdsInPlan, startLocationReads } from './workflowAssetsPreload';
import { useBankErrorReporter } from './WorkflowRunContext'; // P9D #6 (I10)
import { apiErrorText, bankInUse } from './apiErrorText';
import { useFilmPlan } from './FilmPlanContext';
import type { FilmLocation, ImageModelInfo } from '../../types/film';
import type { LocationNeedsResponse } from '../../types/filmLocationAssets';
import ImageComposer from './ImageComposer';
import { ShotRow } from './LocationAssetCard';
import EpisodeOnlyFilter from './EpisodeOnlyFilter';
import { useBankUsage } from './useBankUsage';
import { filterByEpisode } from './bankFilterUtils';
import FilmAssetCard, { type AssetField } from './FilmAssetCard';
import { useBankVars } from './useBankVars';
import { locationsNeedingVisual, visualBatchConfirm } from './locationCardUtils';
import './bank.css';
import './assetCards.css';
import { ASSET_TAB_HEAD } from './assetCardsUi';

type Busy = 'gen' | 'geo' | 'upload' | 'delete' | 'visual' | null;

/**
 * Tab Bối cảnh (đợt 260922 E2-P3 dựng lại theo thẻ TopView). Bối cảnh là dữ liệu CẤP TRUYỆN, dùng lại qua các tập.
 * `onChanged` báo drawer tải lại dòng "Chuẩn bị x/3".
 */
const FilmLocationBank: React.FC<{
  onChanged?: () => void;
  /** `sky` = khung 8 bước: cùng toolbar + FilmAssetCard, thêm views LocationAssetCard; không mount list thứ hai. */
  variant?: 'bank' | 'sky';
  onActionDone?: () => void;
  needs?: LocationNeedsResponse | null;
  /** 261030 B1: ngăn Sửa của màn Tài sản khung 4 bước chỉ mở đúng bối cảnh này (giữ nguyên nút sửa ảnh + ảnh hướng). */
  onlyIds?: string[];
}> = ({ onChanged, variant = 'bank', onActionDone, needs, onlyIds }) => {
  const { storyId, planId } = useFilmPlan();
  const reportError = useBankErrorReporter();
  const vars = useBankVars();
  const [locs, setLocs] = useState<FilmLocation[]>([]);
  const [models, setModels] = useState<ImageModelInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<Record<string, Busy>>({});
  const [studio, setStudio] = useState<FilmLocation | null>(null);
  // 260819: lọc theo tập đang mở — bank gắn với TRUYỆN, truyện dài thì danh sách chỉ có dài thêm.
  const { usage } = useBankUsage(planId);
  const [epOnly, setEpOnly] = useState(true);
  // Bối cảnh vừa thêm tay chưa có scene nào dùng ⇒ bộ lọc "Chỉ tập này" giấu nó ngay khi tạo xong, người dùng tưởng
  // thêm hỏng. Giữ nó trên màn trong phiên này.
  const [recent, setRecent] = useState<string[]>([]);
  const shownLocs = onlyIds ? locs.filter((l) => onlyIds.includes(l.id)) : [
    ...locs.filter((l) => recent.includes(l.id)),
    ...filterByEpisode(locs, usage?.locationIds, { on: epOnly, filterable: !!usage?.filterable.locations, emptyMeansAll: true })
      .filter((l) => !recent.includes(l.id)),
  ];
  // Thêm BẰNG TAY (260806) — nay là thẻ trống ngay đầu lưới thay cho modal.
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  // 260922 E4-P1 — cảnh báo của lượt AI viết Visual Description gần nhất, theo bối cảnh (chỉ trong phiên).
  const [visualWarn, setVisualWarn] = useState<Record<string, string[]>>({});
  const [visualBatch, setVisualBatch] = useState<string | null>(null);

  // P3 F1: chỉ lần nạp ĐẦU (effect mount) lấy từ chỗ giao một lần; `load()` sau thao tác ghi / «Tải lại» luôn gọi mới.
  const load = useCallback(async (first?: unknown) => {
    setLoading(true);
    try { setLocs(await (first === true ? takeWorkflowHandoff(AK.locations(storyId), () => filmApi.listLocations(storyId)) : filmApi.listLocations(storyId))); }
    catch (e: any) { message.error(`Load failed: ${e?.message}`); }
    finally { setLoading(false); }
  }, [storyId]);

  useEffect(() => { load(true); }, [load]);
  // Danh mục model chỉ để dựng menu ▾ — hỏng thì menu chỉ còn mục "mặc định", thẻ vẫn chạy.
  useEffect(() => { takeWorkflowHandoff(AK.models(), () => filmApi.imageStudioModels()).then(setModels).catch(() => setModels([])); }, []);
  // P3 F2: ảnh hướng của bối cảnh THUỘC plan (bank-usage.locationIds) đọc sớm — không chờ `location-needs` (cổng `row` chỉ chặn phần GHI/nhóm). Id ngoài plan không đọc. Idempotent theo khoá
  // (đã đọc sớm từ trang ⇒ không bắn thêm). Chỉ khi `needs` chưa về: có `needs` rồi thì panel tự đọc ngay lúc mount.
  const earlyIds = variant === 'sky' && !needs ? locationIdsInPlan(locs, usage) : [];
  const earlyKey = earlyIds.join(',');
  useEffect(() => { if (planId && earlyKey) startLocationReads(planId, earlyKey.split(','), filmApi); }, [planId, earlyKey]);

  const changed = () => { load(); onChanged?.(); };
  const run = async (id: string, kind: Busy, fn: () => Promise<unknown>, what: string, ok?: string) => {
    setBusy((b) => ({ ...b, [id]: kind }));
    try { await fn(); if (ok) message.success(ok); changed(); }
    catch (e: any) { reportError(e, what); }
    finally { setBusy((b) => { const n = { ...b }; delete n[id]; return n; }); }
  };

  const extract = async () => {
    if (!planId) { message.warning('Chưa chọn plan'); return; }
    try {
      const res = await filmApi.extractLocations(planId);
      message.success(`Đã lấy ${res.length} bối cảnh từ kịch bản`);
      changed();
    } catch (e: any) { reportError(e, 'Lấy bối cảnh từ kịch bản'); }
  };

  const addLocation = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const made = await filmApi.createLocation(storyId, { name, description: newDesc.trim() || undefined });
      setRecent((r) => [made.id, ...r.filter((x) => x !== made.id)]);
      message.success(`Đã thêm "${name}"`);
      setAdding(false); setNewName(''); setNewDesc(''); changed();
    } catch (e: any) { message.error(apiErrorText(e, 'Thêm bối cảnh')); }
  };

  /** Xoá: server trả 409 khi bối cảnh đang được dùng (có ảnh / scene đang dùng) → hỏi lại rồi mới cưỡng chế. */
  const del = async (l: FilmLocation, force = false) => {
    setBusy((b) => ({ ...b, [l.id]: 'delete' }));
    try {
      await filmApi.deleteLocation(l.id, force);
      message.success(`Đã xoá "${l.name}"`);
      changed();
    } catch (e: any) {
      const inUse = bankInUse(e);
      if (!inUse) { message.error(apiErrorText(e, 'Xoá bối cảnh')); return; }
      Modal.confirm({
        title: `"${inUse.name}" đang được dùng`,
        content: <div style={{ fontSize: 13 }}>{inUse.usage.map((u) => <div key={u}>• {u}</div>)}<div style={{ marginTop: 8 }}>Vẫn xoá? Không hoàn lại được.</div></div>,
        okText: 'Vẫn xoá', okButtonProps: { danger: true }, cancelText: 'Huỷ',
        onOk: () => del(l, true),
      });
    } finally { setBusy((b) => { const n = { ...b }; delete n[l.id]; return n; }); }
  };

  const saveField = async (l: FilmLocation, field: AssetField, value: string) => {
    try { await filmApi.updateLocation(l.id, { [field]: value } as any); changed(); }
    catch (e: any) { message.error(apiErrorText(e, 'Lưu bối cảnh')); }
  };

  /** 260922 E4-P1 — AI viết Visual Description. Trả `false` khi lỗi (nút hàng loạt dừng). */
  const genVisual = async (l: FilmLocation): Promise<boolean> => {
    setBusy((b) => ({ ...b, [l.id]: 'visual' }));
    try {
      const r = await filmApi.generateLocationVisual(l.id, planId);
      setLocs((list) => list.map((x) => (x.id === l.id ? { ...x, ...r.location } : x)));
      setVisualWarn((w) => ({ ...w, [l.id]: r.warnings || [] }));
      return true;
    } catch (e: any) { reportError(e, `AI viết Visual Description "${l.name}"`); return false; }
    finally { setBusy((b) => { const n = { ...b }; delete n[l.id]; return n; }); }
  };
  const missingVisual = locationsNeedingVisual(shownLocs);
  const genVisualMissing = async () => {
    let done = 0;
    for (const l of missingVisual) {
      setVisualBatch(`${done + 1}/${missingVisual.length} · ${l.name}`);
      if (!(await genVisual(l))) break;
      done += 1;
    }
    setVisualBatch(null);
    message.success(`Đã viết Visual Description cho ${done}/${missingVisual.length} bối cảnh`);
    onChanged?.();
  };

  /** Menu ▾: gen qua Xưởng ảnh bằng model đã chọn rồi DÙNG LUÔN — nghĩa "Regenerate" của TopView. */
  const genWithModel = (l: FilmLocation, model?: string) => run(l.id, 'gen', async () => {
    const r = await filmApi.imageStudioGenerate('LOCATION', l.id, { model, planId });
    await filmApi.imageStudioChoose(r.variant.id, planId);
    (r.warnings || []).forEach((w) => message.warning(w, 8));
  }, 'Gen ảnh bối cảnh', 'Đã gen và dùng ảnh mới');

  return (
    <div style={vars} className={variant === 'sky' ? 'ac-scope' : undefined}>
      {onlyIds ? null : variant === 'sky' ? (
        <AssetTabHead title={ASSET_TAB_HEAD.locations.title} sub={ASSET_TAB_HEAD.locations.sub} onRefresh={changed} refreshing={loading} testId="ac-head-locations">
          <EpisodeOnlyFilter board on={epOnly} onChange={setEpOnly} shown={shownLocs.length} total={locs.length}
            filterable={!!usage?.filterable.locations} />
          <Popconfirm title="Lấy bối cảnh từ kịch bản của plan này?" description="Bối cảnh đã có giữ nguyên; chỉ thêm cái còn thiếu." okText="Lấy" cancelText="Thôi" onConfirm={extract}>
            <AssetHeadButton>Lấy bối cảnh từ kịch bản</AssetHeadButton>
          </Popconfirm>
          {missingVisual.length > 0 && (
            <Popconfirm title={`Viết mô tả hình ảnh cho ${missingVisual.length} bối cảnh?`} description={<div style={{ maxWidth: 300 }}>{visualBatchConfirm(missingVisual.length)}</div>}
              okText="Viết" cancelText="Thôi" onConfirm={genVisualMissing}>
              <AssetHeadButton disabled={!!visualBatch}>{visualBatch ? `Đang viết ${visualBatch}` : `AI viết mô tả · ${missingVisual.length}`}</AssetHeadButton>
            </Popconfirm>
          )}
          <AssetAddButton onClick={() => setAdding(true)}>Thêm bối cảnh</AssetAddButton>
        </AssetTabHead>
      ) : (
      <div className="bk-head">
        <h3>Bối cảnh</h3>
        <span className="sub">dùng chung cho mọi tập của truyện</span>
        <span className="sp" />
        <EpisodeOnlyFilter on={epOnly} onChange={setEpOnly} shown={shownLocs.length} total={locs.length}
          filterable={!!usage?.filterable.locations} />
        <Popconfirm title="Lấy bối cảnh từ kịch bản của plan này?" description="Bối cảnh đã có giữ nguyên; chỉ thêm cái còn thiếu." okText="Lấy" cancelText="Thôi" onConfirm={extract}>
          <Button size="small">Lấy bối cảnh từ kịch bản</Button>
        </Popconfirm>
        {missingVisual.length > 0 && (
          <Popconfirm title={`Viết Visual Description cho ${missingVisual.length} bối cảnh?`} description={<div style={{ maxWidth: 300 }}>{visualBatchConfirm(missingVisual.length)}</div>}
            okText="Viết" cancelText="Thôi" onConfirm={genVisualMissing}>
            <Button size="small" loading={!!visualBatch}>{visualBatch ? `Đang viết ${visualBatch}` : `Viết Visual Description · ${missingVisual.length}`}</Button>
          </Popconfirm>
        )}
        <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)}>Thêm bối cảnh</Button>
        <Button size="small" icon={<ReloadOutlined />} onClick={changed} loading={loading} />
      </div>
      )}

      {variant === 'sky' ? (
        <div className="ac-locs" data-testid="ac-locs">
          {adding && (
            <div className="ac-card" style={{ maxWidth: 420 }}>
              <div className="ac-add">
                <div className="hd"><b>Bối cảnh mới</b></div>
                <Input size="small" autoFocus placeholder="Tên, vd MORTAIN MANOR FOYER" value={newName} onChange={(e) => setNewName(e.target.value)} onPressEnter={addLocation} />
                <Input.TextArea size="small" autoSize={{ minRows: 2, maxRows: 6 }} placeholder="Ghi chú từ kịch bản (có thể để trống — mô tả hình ảnh viết sau trên bảng)" value={newDesc} onChange={(e) => setNewDesc(e.target.value)} />
                <div className="row">
                  <AssetHeadButton onClick={() => { setAdding(false); setNewName(''); setNewDesc(''); }}>Thôi</AssetHeadButton>
                  <AssetAddButton disabled={!newName.trim()} onClick={addLocation}>Thêm</AssetAddButton>
                </div>
              </div>
            </div>
          )}
          {/* 🔴 Mỗi bối cảnh MỘT panel; dòng `needs` tìm theo CHÍNH l.id ⇒ nhóm hướng + ảnh theo hướng của panel này chỉ thuộc bối cảnh này (không gộp nhóm giữa các bối cảnh). */}
          {shownLocs.map((l) => (
            <WorkflowLocationPanel key={l.id} planId={planId!} location={l}
              row={needs ? (needs.locations.find((x) => x.locationId === l.id) ?? null) : null} needsLoaded={!!needs}
              models={models} busy={busy[l.id] || null} visualWarnings={visualWarn[l.id]}
              onSaveField={(f, v) => saveField(l, f, v)}
              onSaveZones={async (zones) => { await filmApi.updateLocation(l.id, { zones }); changed(); }}
              onGen={() => run(l.id, 'gen', () => filmApi.generateLocationRef(l.id, planId), 'Gen ảnh bối cảnh', 'Đã gen ảnh bối cảnh')}
              onGenVisual={async () => { await genVisual(l); }}
              onGenWithModel={(m) => genWithModel(l, m)}
              onUpload={(file) => run(l.id, 'upload', () => filmApi.imageStudioUpload('LOCATION', l.id, file, planId), 'Tải ảnh lên', 'Đã dùng ảnh tải lên')}
              onOpenStudio={() => setStudio(l)}
              onDelete={() => del(l)}
              onGenGeometry={() => run(l.id, 'geo', () => filmApi.generateLocationGeography(l.id, planId), 'AI viết bố cục', 'Đã có bố cục không gian')}
              onActionDone={() => { onActionDone?.(); onChanged?.(); }} />
          ))}
        </div>
      ) : (
      <div className="bk-grid">
        {adding && (
          <div className="bk-card">
            <div className="bk-inl" style={{ margin: 12 }}>
              <div className="hd"><b>Bối cảnh mới</b></div>
              <Input size="small" autoFocus placeholder="Tên, vd MORTAIN MANOR FOYER" value={newName} onChange={(e) => setNewName(e.target.value)} onPressEnter={addLocation} />
              <Input.TextArea size="small" autoSize={{ minRows: 2, maxRows: 6 }} placeholder="Ghi chú từ kịch bản (có thể để trống — Visual Description viết sau trên thẻ)" value={newDesc} onChange={(e) => setNewDesc(e.target.value)} />
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <Button size="small" onClick={() => { setAdding(false); setNewName(''); setNewDesc(''); }}>Thôi</Button>
                <Button size="small" type="primary" disabled={!newName.trim()} onClick={addLocation}>Thêm</Button>
              </div>
            </div>
          </div>
        )}
        {shownLocs.map((l) => (
          <div key={l.id}>
          <FilmAssetCard kind="location" item={l} models={models} busy={busy[l.id] || null}
            onSaveField={(f, v) => saveField(l, f, v)}
            onSaveZones={async (zones) => { await filmApi.updateLocation(l.id, { zones }); changed(); }}
            onGen={() => run(l.id, 'gen', () => filmApi.generateLocationRef(l.id, planId), 'Gen ảnh bối cảnh', 'Đã gen ảnh bối cảnh')}
            onGenVisual={async () => { await genVisual(l); }} visualWarnings={visualWarn[l.id]}
            onGenWithModel={(m) => genWithModel(l, m)}
            onUpload={(file) => run(l.id, 'upload', () => filmApi.imageStudioUpload('LOCATION', l.id, file, planId), 'Tải ảnh lên', 'Đã dùng ảnh tải lên')}
            onOpenStudio={() => setStudio(l)}
            onDelete={() => del(l)}
            onGenGeometry={() => run(l.id, 'geo', () => filmApi.generateLocationGeography(l.id, planId), 'AI viết bố cục', 'Đã có bố cục không gian')} />
          </div>
        ))}
      </div>
      )}
      {!shownLocs.length && !adding && (
        <div className="bk-dimline" style={{ padding: '24px 0', textAlign: 'center' }}>
          {locs.length
            ? `Tập này không dùng bối cảnh nào trong ${locs.length} bối cảnh của truyện — tắt "Chỉ tập này" để xem.`
            : 'Chưa có bối cảnh — bấm "Lấy bối cảnh từ kịch bản" hoặc "Thêm bối cảnh".'}
        </div>
      )}
      {variant === 'sky' && !onlyIds && needs && needs.unresolved.length > 0 ? (
        <section className="wf-loc" aria-label="Shot chưa xác định được bối cảnh">
          <header className="wf-loc-h"><h3>Shot chưa xác định được bối cảnh</h3><span className="wf-loc-n">{needs.unresolved.length} shot</span></header>
          <div className="wf-loc-body">
            <p className="faint" style={{ fontSize: 12.5, margin: 0 }}>Cảnh của các shot này chưa ghi bối cảnh khớp với Bank nên không gán được hướng.</p>
            <ul className="wf-shots">{needs.unresolved.map((s) => <ShotRow key={s.shotId} shot={s} views={null} />)}</ul>
          </div>
        </section>
      ) : null}

      {studio && (
        <ImageComposer open={!!studio} backend={{ kind: 'studio', ownerType: 'LOCATION', ownerId: studio.id }} planId={planId}
          title={studio.name} onClose={() => setStudio(null)} onChanged={changed}
          bankImages={locs.filter((l) => l.canonicalRefUrl && l.id !== studio.id).map((l) => ({ key: l.id, url: l.canonicalRefUrl!, label: l.name }))} />
      )}
    </div>
  );
};

export default FilmLocationBank;
